import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { Lock, Plus, StickyNote, Target, Trash2 } from "lucide-react";
import {
  createAnnotation, createGoal, deleteAnnotation, deleteGoal, errorMessage, formatMetricValue,
  getAnnotations, getGoals, isoAddMonths, jamaicaToday, metricsUrls, shortDate,
  type AnnotationKind, type AnnotationsResponse, type GoalsResponse, type RangeParams,
} from "@/lib/metrics";
import { AnnotationBadge, EmptyState, ErrorState, TableSkeleton } from "./shared";

const KINDS: AnnotationKind[] = ["note", "campaign", "pricing", "product", "exam", "incident"];

function invalidate(prefix: string) {
  queryClient.invalidateQueries({
    predicate: (q) => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith(prefix),
  });
}

export default function GoalsTab({ range }: { range: RangeParams }) {
  const { toast } = useToast();
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalMetric, setGoalMetric] = useState("");
  const [goalTarget, setGoalTarget] = useState("");
  const [goalDue, setGoalDue] = useState(isoAddMonths(jamaicaToday(), 3));
  const [goalLabel, setGoalLabel] = useState("");

  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDate, setNoteDate] = useState(jamaicaToday());
  const [noteTitle, setNoteTitle] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [noteKind, setNoteKind] = useState<AnnotationKind>("note");

  const { data: goalsData, isLoading: goalsLoading, error: goalsError } = useQuery<GoalsResponse>({
    queryKey: [metricsUrls.goals()],
    queryFn: getGoals,
  });

  const { data: annData, isLoading: annLoading, error: annError } = useQuery<AnnotationsResponse>({
    queryKey: [metricsUrls.annotations(range)],
    queryFn: () => getAnnotations(range),
  });

  const metricDef = goalsData?.metrics.find((m) => m.id === goalMetric);

  const createGoalMutation = useMutation({
    mutationFn: () => {
      const raw = Number(goalTarget);
      const target = metricDef?.unit === "money" ? Math.round(raw * 100) : raw;
      return createGoal({ metric: goalMetric, target, dueAt: goalDue, label: goalLabel.trim() || undefined });
    },
    onSuccess: () => {
      toast({ title: "Goal set" });
      setGoalOpen(false); setGoalMetric(""); setGoalTarget(""); setGoalLabel("");
      invalidate(metricsUrls.goals());
      invalidate("/api/admin/metrics/overview");
    },
    onError: (e) => toast({ title: "Could not set the goal", description: errorMessage(e), variant: "destructive" }),
  });

  const deleteGoalMutation = useMutation({
    mutationFn: (id: string) => deleteGoal(id),
    onSuccess: () => { toast({ title: "Goal removed" }); invalidate(metricsUrls.goals()); invalidate("/api/admin/metrics/overview"); },
    onError: (e) => toast({ title: "Could not remove the goal", description: errorMessage(e), variant: "destructive" }),
  });

  const createNoteMutation = useMutation({
    mutationFn: () => createAnnotation({ date: noteDate, title: noteTitle.trim(), note: noteBody.trim() || undefined, kind: noteKind }),
    onSuccess: () => {
      toast({ title: "Note added" });
      setNoteOpen(false); setNoteTitle(""); setNoteBody(""); setNoteKind("note");
      invalidate("/api/admin/metrics/annotations");
      invalidate("/api/admin/metrics/overview");
      invalidate("/api/admin/metrics/series");
    },
    onError: (e) => toast({ title: "Could not add the note", description: errorMessage(e), variant: "destructive" }),
  });

  const deleteNoteMutation = useMutation({
    mutationFn: (id: string) => deleteAnnotation(id),
    onSuccess: () => {
      toast({ title: "Note removed" });
      invalidate("/api/admin/metrics/annotations");
      invalidate("/api/admin/metrics/overview");
      invalidate("/api/admin/metrics/series");
    },
    onError: (e) => toast({ title: "Could not remove the note", description: errorMessage(e), variant: "destructive" }),
  });

  const goals = goalsData?.goals || [];
  const annotations = [...(annData?.annotations || [])].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="space-y-6" data-testid="tab-goals">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4" />Goals</CardTitle>
              <CardDescription>Pick a number, pick a date, and the console will tell you whether the current pace gets you there.</CardDescription>
            </div>
            <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="button-new-goal"><Plus className="w-4 h-4 mr-1" />New goal</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Set a goal</DialogTitle>
                  <DialogDescription>Targets are read in the metric's own unit. Money is entered in whole Jamaican dollars.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Metric</Label>
                    <Select value={goalMetric} onValueChange={setGoalMetric}>
                      <SelectTrigger data-testid="select-goal-metric"><SelectValue placeholder="Pick a metric" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {(goalsData?.metrics || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">
                      Target{metricDef ? ` (${metricDef.unit === "money" ? "J$" : metricDef.unit})` : ""}
                      {metricDef?.lowerIsBetter ? ", lower is better for this one" : ""}
                    </Label>
                    <Input value={goalTarget} onChange={(e) => setGoalTarget(e.target.value)} placeholder="0" data-testid="input-goal-target" />
                  </div>
                  <div>
                    <Label className="text-xs">Due by</Label>
                    <Input type="date" value={goalDue} onChange={(e) => setGoalDue(e.target.value)} data-testid="input-goal-due" />
                  </div>
                  <div>
                    <Label className="text-xs">Label (optional)</Label>
                    <Input value={goalLabel} onChange={(e) => setGoalLabel(e.target.value)} placeholder="Q1 MRR push" data-testid="input-goal-label" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setGoalOpen(false)}>Cancel</Button>
                  <Button
                    onClick={() => createGoalMutation.mutate()}
                    disabled={createGoalMutation.isPending || !goalMetric || !goalTarget.trim() || !goalDue || !Number.isFinite(Number(goalTarget))}
                    data-testid="button-save-goal"
                  >
                    Set goal
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {goalsLoading ? <TableSkeleton rows={3} cols={4} />
            : goalsError ? <ErrorState error={goalsError} />
              : goals.length === 0 ? (
                <EmptyState title="No goals yet" hint="Set one for MRR, customers or churn and it will appear on the Control Center with a progress bar." />
              ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {goals.map((g) => (
                    <div key={g.id} className="rounded-lg border p-3 space-y-2" data-testid={`goal-${g.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{g.label || g.metric}</div>
                          <div className="text-xs text-muted-foreground tabular-nums">
                            {formatMetricValue(g.value, g.unit)} of {formatMetricValue(g.target, g.unit)}
                            {g.lowerIsBetter ? " (lower is better)" : ""}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Badge className={`border-0 ${g.achieved || g.onTrack ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-destructive/10 text-destructive"}`}>
                            {g.achieved ? "Achieved" : g.onTrack ? "On track" : "Behind"}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => deleteGoalMutation.mutate(g.id)}
                            disabled={deleteGoalMutation.isPending}
                            title="Delete goal"
                            data-testid={`button-delete-goal-${g.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      <Progress value={Math.max(0, Math.min(100, g.progress))} className={g.achieved || g.onTrack ? "" : "[&>div]:bg-destructive"} />
                      <div className="text-xs text-muted-foreground">
                        Started at {formatMetricValue(g.startValue, g.unit)} on {shortDate(g.startsAt)} ·{" "}
                        {g.achieved ? `hit ${shortDate(g.achievedAt)}` : `${g.daysLeft} day${g.daysLeft === 1 ? "" : "s"} left, due ${shortDate(g.dueAt)}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><StickyNote className="w-4 h-4" />Notes on the timeline</CardTitle>
              <CardDescription>
                Notes for {shortDate(range.from)} – {shortDate(range.to)}. They show up as markers on every chart, so a strange month has an explanation next year.
              </CardDescription>
            </div>
            <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="button-new-note"><Plus className="w-4 h-4 mr-1" />New note</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add a note</DialogTitle>
                  <DialogDescription>A price change, a campaign, an outage, a PEP window. Anything that moves the numbers.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Date</Label>
                      <Input type="date" value={noteDate} onChange={(e) => setNoteDate(e.target.value)} data-testid="input-note-date" />
                    </div>
                    <div>
                      <Label className="text-xs">Kind</Label>
                      <Select value={noteKind} onValueChange={(v) => setNoteKind(v as AnnotationKind)}>
                        <SelectTrigger data-testid="select-note-kind"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {KINDS.map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Title</Label>
                    <Input value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} placeholder="Raised the monthly price to J$2,500" data-testid="input-note-title" />
                  </div>
                  <div>
                    <Label className="text-xs">Note (optional)</Label>
                    <Textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} rows={3} placeholder="Anything future-you will want to know." data-testid="input-note-body" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setNoteOpen(false)}>Cancel</Button>
                  <Button
                    onClick={() => createNoteMutation.mutate()}
                    disabled={createNoteMutation.isPending || !noteTitle.trim() || !noteDate}
                    data-testid="button-save-note"
                  >
                    Add note
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {annLoading ? <TableSkeleton rows={4} cols={3} />
            : annError ? <ErrorState error={annError} />
              : annotations.length === 0 ? (
                <EmptyState title="Nothing noted in this period" hint="Add a note the day you change a price or launch a campaign; it takes ten seconds and saves an argument later." />
              ) : (
                <ul className="space-y-2">
                  {annotations.map((a) => (
                    <li key={a.id} className="flex items-start gap-3 text-sm border-b last:border-0 pb-2 last:pb-0" data-testid={`note-${a.id}`}>
                      <span className="text-xs text-muted-foreground w-16 shrink-0 pt-0.5 tabular-nums">{shortDate(a.date)}</span>
                      <AnnotationBadge kind={a.kind} />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{a.title}</div>
                        {a.note ? <div className="text-xs text-muted-foreground">{a.note}</div> : null}
                      </div>
                      {a.auto ? (
                        <Badge variant="outline" className="font-normal gap-1 shrink-0" title="Added automatically from a campaign or the academic calendar">
                          <Lock className="w-3 h-3" />automatic
                        </Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0"
                          onClick={() => deleteNoteMutation.mutate(a.id)}
                          disabled={deleteNoteMutation.isPending}
                          title="Delete note"
                          data-testid={`button-delete-note-${a.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
        </CardContent>
      </Card>
    </div>
  );
}
